import { MapPin, Phone, Clock, Mail, Send, MessageCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';

export default function ContactPage() {
  const { t } = useLanguage();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    alert(t('Appointment request sent! We will contact you soon.', 'Demande de rendez-vous envoyée! Nous vous contacterons bientôt.'));
  };

  return (
    <div className="min-h-screen pt-28 pb-20 bg-gradient-to-br from-blue-50 via-white to-amber-50">
      <div className="container mx-auto px-4">
        {/* Hero Section */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-blue-900 mb-4">
            {t('Contact Clinique Mains Douces Plus', 'Contactez Clinique Mains Douces Plus')}
          </h1>
          <p className="text-base sm:text-lg text-gray-600">
            {t(
              'We are here to answer all your questions and take care of your health. Located in Tradex Simbock, Yaoundé.',
              'Nous sommes là pour répondre à toutes vos questions et prendre soin de votre santé. Situé à Tradex Simbock, Yaoundé.'
            )}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Left Column */}
          <div>
            {/* Contact Cards */}
            <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 mb-6">
              <h2 className="text-2xl font-bold text-blue-900 mb-6">
                {t('Visit Us in Simbock', 'Visitez-Nous à Simbock')}
              </h2>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="bg-blue-600 p-3 rounded-lg flex-shrink-0">
                    <MapPin className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-blue-900 mb-1 text-lg">
                      {t('Our Location', 'Notre Emplacement')}
                    </h3>
                    <p className="text-gray-700">
                      Tradex Simbock, {t('Yaoundé, Cameroon', 'Yaoundé, Cameroun')}
                    </p>
                    <p className="text-sm text-amber-600 mt-1">
                      {t('Opposite the main market', 'En face du marché principal')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-blue-600 p-3 rounded-lg flex-shrink-0">
                    <Phone className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-blue-900 mb-1 text-lg">
                      {t('Phone', 'Téléphone')}
                    </h3>
                    <a href="tel:+237690640369" className="text-blue-600 hover:text-blue-800 font-medium text-xl block">
                      +237 690 640 369
                    </a>
                    <a href="tel:+237653474614" className="text-blue-600 hover:text-blue-800 font-medium text-lg">
                      +237 653 474 614
                    </a>
                    <p className="text-sm text-gray-500 mt-1">
                      {t('Available 24/7 for emergencies', 'Disponible 24h/24 pour les urgences')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-blue-600 p-3 rounded-lg flex-shrink-0">
                    <Mail className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-blue-900 mb-1 text-lg">
                      {t('Email', 'Email')}
                    </h3>
                    <a 
                      href="mailto:cliniquemainsdoucesplus@gmail.com"
                      className="text-blue-600 hover:text-blue-800 font-medium text-lg block break-all"
                    >
                      cliniquemainsdoucesplus@gmail.com
                    </a>
                    <p className="text-sm text-gray-500 mt-1">
                      {t('Click to send us an email', 'Cliquez pour nous envoyer un email')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="bg-blue-600 p-3 rounded-lg flex-shrink-0">
                    <Clock className="text-white" size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-blue-900 mb-1 text-lg">
                      {t('Opening Hours', 'Heures d\'Ouverture')}
                    </h3>
                    <p className="text-gray-700">
                      {t('Monday - Friday: 8:00 AM - 6:00 PM', 'Lundi - Vendredi: 8h00 - 18h00')}
                    </p>
                    <p className="text-gray-700">
                      {t('Saturday: 9:00 AM - 3:00 PM', 'Samedi: 9h00 - 15h00')}
                    </p>
                    <p className="text-amber-600 font-semibold">
                      {t('Sunday: Emergencies only', 'Dimanche: Urgences uniquement')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Why Choose Us */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-3xl p-6 sm:p-8 text-white mb-6">
              <h3 className="text-xl font-bold mb-4">
                {t('Why Choose Our Clinic?', 'Pourquoi Choisir Notre Clinique?')}
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <span className="text-amber-300">✓</span>
                  <span>{t('Conveniently located in Tradex Simbock', 'Idéalement situé à Tradex Simbock')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-300">✓</span>
                  <span>{t('Easy access from all neighborhoods', 'Accès facile depuis tous les quartiers')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-300">✓</span>
                  <span>{t('Ample parking space available', 'Espace de stationnement disponible')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-300">✓</span>
                  <span>{t('Modern medical equipment', 'Équipement médical moderne')}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-300">✓</span>
                  <span>{t('Qualified multilingual staff', 'Personnel qualifié multilingue')}</span>
                </li>
              </ul>
            </div>

            {/* Email Card */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-3xl p-6 sm:p-8 text-white mb-6">
              <div className="flex items-center gap-3 mb-4">
                <Mail size={32} />
                <h3 className="text-xl font-bold">{t('Send us an Email', 'Envoyez-nous un Email')}</h3>
              </div>
              <p className="mb-6 text-blue-100">
                {t(
                  'For detailed inquiries or to send documents, email us directly.',
                  'Pour des demandes détaillées ou envoyer des documents, écrivez-nous directement.'
                )}
              </p>
              <a
                href="mailto:cliniquemainsdoucesplus@gmail.com?subject=Contact%20from%20Website&body=Hello%20Clinique%20Mains%20Douces%20Plus%2C%0D%0A%0D%0AI%20would%20like%20to%20inquire%20about%3A%0D%0A%0D%0A%0D%0AThank%20you."
                className="block w-full bg-white text-blue-600 text-center px-6 py-4 rounded-xl font-semibold hover:bg-blue-50 transition transform hover:scale-105 text-lg"
              >
                {t('Send Email', 'Envoyer Email')}
              </a>
            </div>

            {/* WhatsApp Card */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-3xl p-6 sm:p-8 text-white">
              <div className="flex items-center gap-3 mb-4">
                <MessageCircle size={32} />
                <h3 className="text-xl font-bold">WhatsApp</h3>
              </div>
              <p className="mb-6 text-green-100">
                {t(
                  'For quick responses, contact us via WhatsApp. We typically respond within minutes.',
                  'Pour des réponses rapides, contactez-nous via WhatsApp. Nous répondons généralement en quelques minutes.'
                )}
              </p>
              <a
                href="https://wa.me/237653474614?text=Hello%20Clinique%20Mains%20Douces%20Plus%2C%20I%20would%20like%20to%20inquire%20about%20your%20services."
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-white text-green-600 text-center px-6 py-4 rounded-xl font-semibold hover:bg-green-50 transition transform hover:scale-105 text-lg"
              >
                {t('Chat on WhatsApp', 'Discuter sur WhatsApp')}
              </a>
            </div>
          </div>

          {/* Right Column - Appointment Form */}
          <div>
            <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 sticky top-28">
              <h2 className="text-2xl font-bold text-blue-900 mb-6">
                {t('Book an Appointment', 'Prendre Rendez-vous')}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('Full Name', 'Nom Complet')} *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    placeholder={t('John Doe', 'Jean Dupont')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('Phone Number', 'Numéro de Téléphone')} *
                  </label>
                  <input
                    type="tel"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    placeholder="+237 6XX XXX XXX"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('Email (Optional)', 'Email (Optionnel)')}
                  </label>
                  <input
                    type="email"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    placeholder="example@email.com"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('Preferred Date', 'Date Préférée')} *
                    </label>
                    <input
                      type="date"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('Preferred Time', 'Heure Préférée')} *
                    </label>
                    <select
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    >
                      <option value="">{t('Select time', 'Choisir heure')}</option>
                      <option value="morning">{t('Morning (8:00 - 12:00)', 'Matin (8h - 12h)')}</option>
                      <option value="afternoon">{t('Afternoon (12:00 - 18:00)', 'Après-midi (12h - 18h)')}</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('Reason for Visit', 'Motif de la Visite')}
                  </label>
                  <textarea
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                    placeholder={t('Briefly describe your symptoms...', 'Décrivez brièvement vos symptômes...')}
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-4 rounded-xl font-semibold text-lg transition transform hover:scale-105 shadow-lg flex items-center justify-center gap-2"
                >
                  <Send size={20} />
                  {t('Request Appointment', 'Demander Rendez-vous')}
                </button>
              </form>

              <p className="text-xs text-gray-500 text-center mt-4">
                {t('* Required fields', '* Champs obligatoires')}
              </p>

              {/* Email Note */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-600 text-center">
                  {t('You can also email us directly at:', 'Vous pouvez aussi nous envoyer un email à:')}
                </p>
                <a 
                  href="mailto:cliniquemainsdoucesplus@gmail.com"
                  className="block text-center text-blue-600 hover:text-blue-800 font-semibold mt-2 break-all"
                >
                  cliniquemainsdoucesplus@gmail.com
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Map Section */}
        <div className="mt-12 bg-white rounded-3xl shadow-xl p-6 overflow-hidden">
          <h2 className="text-2xl font-bold text-blue-900 mb-4">
            {t('Find Us in Simbock', 'Trouvez-Nous à Simbock')}
          </h2>
          <div className="aspect-video bg-gray-200 rounded-2xl overflow-hidden">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3989.6583697200855!2d11.495678!3d3.866667!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zM8KwNTEnMjAuMCJOIDExwrAyOSc0NC40IkU!5e0!3m2!1sen!2scm!4v1620000000000!5m2!1sen!2scm"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              title="Clinique Mains Douces Plus Location"
            ></iframe>
          </div>
          <p className="text-center text-gray-600 mt-4">
            <span className="font-semibold">{t('Address:', 'Adresse:')}</span> Tradex Simbock, {t('Yaoundé, Cameroon', 'Yaoundé, Cameroun')}
          </p>
        </div>
      </div>
    </div>
  );
}